import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { verifierJeton, definirMotDePasse } from './invitation.api';
import { notify } from '../../components/Utils/Notifications';
import ErrorState from '../ErrorState/ErrorState';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';

// Le lien d'invitation ne porte pas le pays (C4, risque 2 du plan) : on
// retombe sur le dernier pays choisi dans ce navigateur. Un jeton valide émis
// pour un autre pays revient alors en refus, indistinguable d'un jeton mort.
const resoudrePays = () => localStorage.getItem('selectedCountryCode') || 'sn';

const champClasse = 'block w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all';

const InvitationMotDePasse = () => {
    const { jeton } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [etat, setEtat] = useState({ statut: 'chargement' });
    const [motDePasse, setMotDePasse] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [erreurFormulaire, setErreurFormulaire] = useState('');

    useEffect(() => {
        let annule = false;
        verifierJeton(jeton, resoudrePays())
            .then((data) => {
                if (annule) return;
                // Aucune branche sur la raison du refus : le proxy rend la même
                // charge {valide:false, message} pour un jeton expiré, déjà
                // utilisé, inconnu ou malformé (anti-énumération Odoo).
                setEtat(data.valide
                    ? { statut: 'formulaire', nom: data.nom }
                    : { statut: 'refus', message: data.message });
            })
            .catch(() => {
                // 400/429/502 ou absence de réponse : jamais confondu avec un
                // refus de jeton, sinon un incident proxy annoncerait à tous
                // les employés que leur lien est mort.
                if (!annule) setEtat({ statut: 'erreur' });
            });
        return () => { annule = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jeton]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErreurFormulaire('');
        if (motDePasse !== confirmation) {
            setErreurFormulaire(t('invitationPortail.confirmationDifferente'));
            return; // aucun appel réseau émis
        }

        const country = resoudrePays();
        try {
            const data = await definirMotDePasse(jeton, motDePasse, country);
            if (data.ok) {
                // Sans ce réglage, Login.js détourne l'employé sur /countryselect
                // faute de pays enregistré (risque du plan).
                localStorage.setItem('selectedCountryCode', country);
                notify(t('invitationPortail.succes'), '', 2);
                navigate('/login', { replace: true });
                return;
            }
            // Jeton mort entre les deux appels : même traitement que le refus initial.
            setEtat({ statut: 'refus', message: data.message });
        } catch (err) {
            const reponse = err?.response;
            if (reponse?.status === 400 && reponse?.data?.message) {
                // Mot de passe trop court côté Odoo : bandeau local, jeton et
                // saisie conservés, l'employé n'a pas à tout retaper.
                setErreurFormulaire(reponse.data.message);
            } else {
                // Clé hors allowlist (notre bug), 429, ou proxy injoignable.
                setEtat({ statut: 'erreur' });
            }
        }
    };

    if (etat.statut === 'chargement') {
        return <LoadingBarWithStyles />;
    }

    if (etat.statut === 'refus' || etat.statut === 'erreur') {
        const estRefus = etat.statut === 'refus';
        return (
            <ErrorState
                title={t(estRefus ? 'invitationPortail.refusTitre' : 'invitationPortail.erreurTitre')}
                description={estRefus ? etat.message : t('invitationPortail.erreurDescription')}
                actionTo="/login"
                actionLabel={t('invitationPortail.retourConnexion')}
                tone={estRefus ? 'orange' : 'cyan'}
            />
        );
    }

    return (
        <div className="w-full flex items-center justify-center p-8 min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
            <div className="w-full max-w-md">
                <div className="bg-white shadow-lg rounded-xl p-8">
                    <div className="mb-4 text-4xl text-[--color-primary] font-extrabold">YROS</div>

                    <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('invitationPortail.bienvenue', { nom: etat.nom })}</h2>
                    <p className="text-gray-600 mb-6">{t('invitationPortail.consigne')}</p>

                    {erreurFormulaire && (
                        <div role="alert" className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                            {erreurFormulaire}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="nouveauMotDePasse" className="block text-sm font-medium text-gray-700 mb-2">{t('invitationPortail.nouveauMotDePasseLabel')}</label>
                            <input type="password" id="nouveauMotDePasse" value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} required className={champClasse} />
                        </div>
                        <div>
                            <label htmlFor="confirmationMotDePasse" className="block text-sm font-medium text-gray-700 mb-2">{t('invitationPortail.confirmationLabel')}</label>
                            <input type="password" id="confirmationMotDePasse" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} required className={champClasse} />
                        </div>
                        <button type="submit" className="w-full bg-[--color-primary] hover:bg-[--color-orange] text-white py-3 px-4 rounded-lg font-medium transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0">
                            {t('invitationPortail.validerBouton')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default InvitationMotDePasse;
